/**
 * Loocator analytics helper.
 *
 * - Sends events to Google Analytics 4 (via window.gtag) if configured
 *   through REACT_APP_GA_MEASUREMENT_ID.
 * - Also mirrors events to our own /api/analytics/track endpoint so the
 *   admin dashboard can render user-count and geography breakdowns without
 *   requiring the GA UI.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const gaEnabled = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

export function trackPageView(path) {
  const url = path || (typeof window !== "undefined" ? window.location.pathname : "");
  if (gaEnabled() && window.__GA_ID__) {
    window.gtag("event", "page_view", {
      page_path: url,
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
    });
  }
  // Backend mirror
  postBackend({ event: "page_view", module: pathToModule(url), meta: { path: url } });
}

export function trackEvent(event, params = {}) {
  if (gaEnabled()) {
    window.gtag("event", event, params);
  }
  const { lat, lng, module, ...meta } = params;
  postBackend({ event, module: module || null, lat, lng, meta });
}

function pathToModule(path) {
  if (!path) return "map";
  if (path === "/" || path.startsWith("/map")) return "map";
  if (path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/auth")) return "auth";
  if (path.startsWith("/admin")) return "admin";
  if (path.startsWith("/about")) return "about";
  if (path.startsWith("/favorites")) return "favorites";
  if (path.startsWith("/profile")) return "profile";
  if (path.startsWith("/add-restroom")) return "add_restroom";
  if (path.startsWith("/share")) return "sos_share";
  return "other";
}

function postBackend(payload) {
  // Fire-and-forget; never block UI on analytics
  try {
    axios.post(`${API}/analytics/track`, payload, { headers: authHeaders(), timeout: 4000 }).catch(() => {});
  } catch (_) {
    /* swallow */
  }
}

// ---- Client-error beacon --------------------------------------------------

const _reportedErrorKeys = new Set();

/**
 * Beacon a client-side problem to the admin dashboard.
 * Deduplicates by (kind, message) so a runaway loop doesn't drown the DB.
 */
export function trackError(kind, message, extra = {}) {
  const key = `${kind}::${(message || "").slice(0, 80)}`;
  if (_reportedErrorKeys.has(key)) return;
  _reportedErrorKeys.add(key);
  // Slow-decay cache so the same error can be re-reported after a while.
  setTimeout(() => _reportedErrorKeys.delete(key), 5 * 60 * 1000);
  try {
    axios
      .post(
        `${API}/analytics/error`,
        {
          kind,
          message: (message || "").slice(0, 500),
          path: typeof window !== "undefined" ? window.location.pathname : "",
          module: extra.module || null,
          meta: extra.meta || {},
        },
        { headers: authHeaders(), timeout: 4000 }
      )
      .catch(() => {});
  } catch (_) {
    /* swallow */
  }
}

let _globalErrorHandlersInstalled = false;
export function installGlobalErrorHandlers() {
  if (_globalErrorHandlersInstalled || typeof window === "undefined") return;
  _globalErrorHandlersInstalled = true;

  window.addEventListener("error", (e) => {
    // Ignore ResizeObserver noise
    if (e && e.message && e.message.includes("ResizeObserver loop")) return;
    trackError("js_error", e?.message || "unknown", {
      meta: { source: e?.filename, line: e?.lineno, col: e?.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e?.reason && (e.reason.message || String(e.reason))) || "unhandled promise rejection";
    trackError("promise_rejection", msg);
  });

  window.addEventListener("offline", () => {
    trackError("disconnect", "network offline", { meta: { at: Date.now() } });
  });

  // Log when the page becomes visible again after being hidden — a rough
  // signal that the user backgrounded / lost focus of the tab.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      trackError("disconnect", "tab hidden", { meta: { at: Date.now() } });
    }
  });
}
